import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useTheme } from "@/context/ThemeContext";

export default function ContactContent() {
  const { t } = useTranslation("home");
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";
  const [selectedType, setSelectedType] = useState("feature");
  const [feedback, setFeedback] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [validationError, setValidationError] = useState("");

  const feedbackTypes = [
    { id: "feature", labelKey: "contact.type_feature" },
    { id: "bug", labelKey: "contact.type_bug" },
    { id: "account", labelKey: "contact.type_account" },
    { id: "other", labelKey: "contact.type_other" },
  ];

  const handleTextChange = (value: string) => {
    if (value.length <= 500) {
      setFeedback(value);
      setCharCount(value.length);
      if (validationError) setValidationError("");
    }
  };

  const validateFeedback = (text: string) => {
    const chineseChars = (text.match(/[\u4e00-\u9fff]/g) || []).length;
    const englishLetters = (text.match(/[a-zA-Z]/g) || []).length;
    return chineseChars >= 10 || englishLetters >= 50;
  };

  return (
    <main className="flex-1 mx-auto w-full max-w-5xl px-4 sm:px-6 pt-[22px] md:pt-[6px] pb-8 md:pb-12">
      {/* 社群與私信 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-10"
      >
        <div className="flex items-center gap-2 mb-5">
          <div
            className={`w-1 h-5 rounded-full ${
              isLight ? "bg-[#2d2d2d]" : "bg-gold-500"
            }`}
          />
          <h2
            className={`text-base font-semibold ${
              isLight ? "text-[#1c1c1e]" : "text-white"
            }`}
          >
            社群與私信
          </h2>
        </div>

        <div className="flex flex-col gap-4">
          {/* 上块 — 社群交流 */}
          <div
            className={`rounded-2xl border p-4 sm:p-6 flex flex-col gap-3 ${
              isLight
                ? "border-black/10 bg-white/70"
                : "border-white/10 bg-cosmic-900/40"
            }`}
          >
            {/* 标签 */}
            <span
              className={`inline-flex self-start px-3 py-1 rounded-full text-xs font-medium ${
                isLight
                  ? "bg-[#2d2d2d]/10 text-[#2d2d2d] border border-[#2d2d2d]/20"
                  : "bg-gold-500/15 text-gold-400 border border-gold-500/25"
              }`}
            >
              社群交流
            </span>

            {/* 标题 */}
            <h3
              className={`text-base font-semibold ${
                isLight ? "text-[#1c1c1e]" : "text-white"
              }`}
            >
              加入交流群
            </h3>

            {/* 描述 */}
            <p
              className={`text-xs leading-relaxed ${
                isLight ? "text-[#6e6e73]" : "text-silver-500"
              }`}
            >
              一起交流 AI、产品与命理体验。
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* 微信群 */}
              <div
                className={`rounded-xl border p-4 sm:p-5 flex flex-col items-center gap-2 ${
                  isLight
                    ? "border-black/5 bg-white"
                    : "border-white/5 bg-cosmic-950/60"
                }`}
              >
                <div className="flex items-center gap-2">
                  <i className="ri-wechat-fill text-lg text-[#07c160]" />
                  <span
                    className={`text-sm font-medium ${
                      isLight ? "text-[#1c1c1e]" : "text-white"
                    }`}
                  >
                    微信群
                  </span>
                </div>
                <div className="w-full max-w-[220px] sm:max-w-[240px] aspect-square rounded-lg overflow-hidden bg-white">
                  <img
                    src="https://static.readdy.ai/image/367a1a87f5f967689a35d528ef258292/d828b58cc3695fb134f5b6c66a85b305.png"
                    alt="微信群二维码"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* 小红书群 */}
              <div
                className={`rounded-xl border p-4 sm:p-5 flex flex-col items-center gap-2 ${
                  isLight
                    ? "border-black/5 bg-white"
                    : "border-white/5 bg-cosmic-950/60"
                }`}
              >
                <div className="flex items-center gap-2">
                  <i className="ri-book-open-fill text-lg text-[#ff2442]" />
                  <span
                    className={`text-sm font-medium ${
                      isLight ? "text-[#1c1c1e]" : "text-white"
                    }`}
                  >
                    小红书群
                  </span>
                </div>
                <div className="w-full max-w-[220px] sm:max-w-[240px] aspect-square rounded-lg overflow-hidden bg-white">
                  <img
                    src="https://storage.helloreaddy.io/project_files/aaa23fc8-cc75-4d34-a6cd-2966d2d720c2/97aa814f-a5c8-41ea-8445-5d563ae3106b_compressed_xiaohongshu_qun.webp?v=2"
                    alt="小红书群二维码"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 下块 — 个人微信 */}
          <div
            className={`rounded-2xl border p-4 sm:p-6 flex flex-col gap-3 ${
              isLight
                ? "border-black/10 bg-white/70"
                : "border-white/10 bg-cosmic-900/40"
            }`}
          >
            {/* 标签 */}
            <span
              className={`inline-flex self-start px-3 py-1 rounded-full text-xs font-medium ${
                isLight
                  ? "bg-[#07c160]/10 text-[#07c160] border border-[#07c160]/20"
                  : "bg-[#07c160]/15 text-[#34d058] border border-[#07c160]/25"
              }`}
            >
              个人微信
            </span>

            {/* 标题 */}
            <h3
              className={`text-base font-semibold ${
                isLight ? "text-[#1c1c1e]" : "text-white"
              }`}
            >
              添加我们的微信
            </h3>

            {/* 描述 */}
            <p
              className={`text-xs leading-relaxed ${
                isLight ? "text-[#6e6e73]" : "text-silver-500"
              }`}
            >
              有事沒事多聊聊，扫码添加我們好友。
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* 二维码卡片 - 快快查字典攻城狮 */}
              <div
                className={`rounded-xl border overflow-hidden flex flex-col items-center ${
                  isLight
                    ? "border-black/5 bg-white"
                    : "border-white/5 bg-cosmic-950/60"
                }`}
              >
                <div className="w-full p-4 flex flex-col items-center gap-3">
                  <div className="w-full max-w-[220px] sm:max-w-[240px] aspect-square relative">
                    <img
                      src="https://storage.helloreaddy.io/project_files/aaa23fc8-cc75-4d34-a6cd-2966d2d720c2/a0112b5b-364e-4976-bdea-f25d2bb64e4e_compressed_weixin1.webp"
                      alt="微信二维码 - 快快查字典攻城狮"
                      className="w-full h-full object-contain rounded-lg"
                    />
                  </div>
                </div>
                <p
                  className={`text-xs text-center pb-3 px-2 ${
                    isLight ? "text-[#6e6e73]/60" : "text-silver-500/60"
                  }`}
                >
                  微信号：快快查字典攻城狮
                </p>
              </div>

              {/* 二维码卡片 - 后山人的生活 */}
              <div
                className={`rounded-xl border overflow-hidden flex flex-col items-center ${
                  isLight
                    ? "border-black/5 bg-white"
                    : "border-white/5 bg-cosmic-950/60"
                }`}
              >
                <div className="w-full p-4 flex flex-col items-center gap-3">
                  <div className="w-full max-w-[220px] sm:max-w-[240px] aspect-square relative">
                    <img
                      src="https://storage.helloreaddy.io/project_files/aaa23fc8-cc75-4d34-a6cd-2966d2d720c2/bc5112c5-2807-45dd-8596-4a121be28825_compressed_weixin2.webp"
                      alt="微信二维码 - 后山人的生活"
                      className="w-full h-full object-contain rounded-lg"
                    />
                  </div>
                </div>
                <p
                  className={`text-xs text-center pb-3 px-2 ${
                    isLight ? "text-[#6e6e73]/60" : "text-silver-500/60"
                  }`}
                >
                  微信号：后山人的生活
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Contact Methods */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-10"
      >
        <div className="flex items-center gap-2 mb-5">
          <div
            className={`w-1 h-5 rounded-full ${
              isLight ? "bg-[#2d2d2d]" : "bg-gold-500"
            }`}
          />
          <h2
            className={`text-base font-semibold ${
              isLight ? "text-[#1c1c1e]" : "text-white"
            }`}
          >
            郵件聯繫
          </h2>
        </div>

        <div className="flex flex-col gap-3">
          {/* Email */}
          <div
            className={`rounded-2xl border p-5 flex items-center gap-4 ${
              isLight
                ? "border-black/10 bg-white/70"
                : "border-white/10 bg-cosmic-900/40"
            }`}
          >
            <div
              className={`w-11 h-11 rounded-xl border flex items-center justify-center flex-shrink-0 ${
                isLight
                  ? "bg-[#2d2d2d]/10 border-[#2d2d2d]/20"
                  : "bg-gold-500/10 border-gold-500/20"
              }`}
            >
              <i
                className={`ri-mail-send-line text-2xl ${
                  isLight ? "text-[#2d2d2d]" : "text-gold-500"
                }`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-semibold ${
                  isLight ? "text-[#1c1c1e]" : "text-white"
                }`}
              >
                {t("contact.email_label")}
              </p>
              <p
                className={`text-xs mt-0.5 ${
                  isLight ? "text-[#6e6e73]" : "text-silver-500"
                }`}
              >
                {t("contact.email_address")}
                <br />
                {t("contact.email_reply")}
              </p>
            </div>
            <a
              href="mailto:dazhuotech@gmail.com"
              className={`px-3.5 py-1.5 rounded-xl text-xs font-medium border transition-colors whitespace-nowrap flex-shrink-0 cursor-pointer ${
                isLight
                  ? "bg-[#2d2d2d]/10 border-[#2d2d2d]/20 text-[#2d2d2d] hover:bg-[#2d2d2d]/20"
                  : "bg-gold-500/10 border-gold-500/20 text-gold-400 hover:bg-gold-500/20"
              }`}
            >
              {t("contact.send_email")}
            </a>
          </div>
        </div>
      </motion.div>

      {/* 反馈服务端接口未接入前先隐藏（区块代码保留，接入后把 false 改回 true 并闭合括号） */}
      {false && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="flex items-center gap-2 mb-5">
          <div
            className={`w-1 h-5 rounded-full ${
              isLight ? "bg-[#2d2d2d]" : "bg-gold-500"
            }`}
          />
          <h2
            className={`text-base font-semibold ${
              isLight ? "text-[#1c1c1e]" : "text-white"
            }`}
          >
            {t("contact.feedback_title")}
          </h2>
        </div>

        <div
          className={`rounded-2xl border overflow-hidden ${
            isLight
              ? "border-black/10 bg-white/70"
              : "border-white/10 bg-cosmic-900/40"
          }`}
        >
          <form
            id="contact-feedback"
            data-readdy-form
            action="https://readdy.ai/api/form/d89utnnial9olsuov5hg"
            method="POST"
            encType="application/x-www-form-urlencoded"
            onSubmit={(e) => {
              e.preventDefault();
              if (!validateFeedback(feedback)) {
                setValidationError("麻烦把问题尽可能的描述清楚！");
                return;
              }
              setValidationError("");
              if (feedback.length > 500) return;
              const form = e.currentTarget;
              const formData = new FormData(form);
              fetch(form.action, {
                method: "POST",
                body: new URLSearchParams(formData as never),
              })
                .then(() => {
                  setSubmitted(true);
                  setFeedback("");
                  setCharCount(0);
                  setTimeout(() => setSubmitted(false), 3000);
                })
                .catch(() => {
                  alert(t("contact.submit_error"));
                });
            }}
          >
            {/* Feedback Type Tags */}
            <div className="px-5 pt-5 pb-3">
              <div className="flex gap-2 mb-4 flex-wrap">
                {feedbackTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setSelectedType(type.id)}
                    name="feedback_type"
                    value={type.id}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
                      selectedType === type.id
                        ? isLight
                          ? "bg-[#2d2d2d]/15 border border-[#2d2d2d]/30 text-[#2d2d2d]"
                          : "bg-gold-500/15 border border-gold-500/30 text-gold-400"
                        : isLight
                        ? "bg-black/5 border border-black/10 text-[#6e6e73] hover:bg-black/10 hover:text-[#1c1c1e]"
                        : "bg-white/5 border border-white/10 text-silver-500 hover:bg-white/10 hover:text-silver-300"
                    }`}
                  >
                    {t(type.labelKey)}
                  </button>
                ))}
              </div>
              <input type="hidden" name="feedback_type" value={selectedType} />

              <textarea
                name="feedback_content"
                value={feedback}
                onChange={(e) => handleTextChange(e.target.value)}
                placeholder={t("contact.feedback_placeholder")}
                maxLength={500}
                rows={5}
                className={`w-full bg-transparent text-sm outline-none resize-none leading-relaxed ${
                  isLight
                    ? "text-[#1c1c1e] placeholder:text-[#6e6e73]/50"
                    : "text-white placeholder:text-silver-500/50"
                }`}
              />

              <div className="flex items-center justify-between mt-2">
                <div className="flex flex-col">
                  <span
                    className={`text-xs ${
                      isLight ? "text-[#6e6e73]/60" : "text-silver-500/60"
                    }`}
                  >
                    {t("contact.char_count", { count: charCount })}
                  </span>
                  {validationError && (
                    <span className="text-xs text-red-500 mt-1">{validationError}</span>
                  )}
                </div>
                {submitted && (
                  <span className="text-xs text-green-400 flex items-center gap-1">
                    <i className="ri-check-line" />
                    {t("contact.submit_success")}
                  </span>
                )}
              </div>
            </div>

            <div className="px-5 pb-5">
              <button
                type="submit"
                disabled={!feedback.trim() || submitted}
                className={`w-full py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                  feedback.trim() && !submitted
                    ? isLight
                      ? "bg-[#4a4a4c] text-white hover:bg-[#3a3a3c]"
                      : "bg-gold-500 text-cosmic-950 hover:bg-gold-400"
                    : isLight
                    ? "bg-black/10 text-[#6e6e73] cursor-not-allowed"
                    : "bg-white/10 text-silver-500 cursor-not-allowed"
                }`}
              >
                <i className="ri-send-plane-line mr-2" />
                {submitted ? t("contact.submitted") : t("contact.submit")}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
      )}
    </main>
  );
}